import { effect, Injectable, inject } from '@angular/core';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';

import { StorageKey, StorageService } from '@zopup/zop-shared';

/**
 * Centralized Keycloak session handling.
 *
 * Why this exists:
 * - With `onLoad: 'login-required'`, Keycloak often redirects the browser during logout/session-expiry.
 * - That means routed components may never observe `KEYCLOAK_EVENT_SIGNAL` events (e.g., AuthLogout).
 *
 * This service is instantiated at app bootstrap and:
 * - Patches `keycloak.logout()` to clear local storage before redirect.
 * - Clears local storage on refresh/auth errors.
 * - Uses a small sessionStorage marker to make logout clearing resilient across reloads.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakSessionSyncService {
    private readonly keycloak = inject(Keycloak);
    private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
    private readonly storageService = inject(StorageService);

    private readonly logoutMarkerKey = 'zop_kc_logout_pending';

    constructor() {
        this.applyPendingLogoutCleanup();
        this.patchLogout();
        this.attachEventHandlers();
    }

    private applyPendingLogoutCleanup(): void {
        // If a logout redirect happened, we may re-enter the app with old localStorage.
        // Ensure we clean up at the next bootstrap.
        try {
            const pending = sessionStorage.getItem(this.logoutMarkerKey);
            if (pending) {
                this.clearStoragePreservingRedirectUrl(false);
                sessionStorage.removeItem(this.logoutMarkerKey);
            }
        } catch {
            // ignore
        }
    }

    private patchLogout(): void {
        const keycloakAny = this.keycloak as unknown as { logout?: (options?: any) => Promise<any> };
        const originalLogout = keycloakAny.logout?.bind(this.keycloak);

        if (!originalLogout) {
            return;
        }

        keycloakAny.logout = async (options?: any): Promise<any> => {
            // Mark it first so even if the browser navigates quickly, we can clean up on next load.
            try {
                sessionStorage.setItem(this.logoutMarkerKey, '1');
            } catch {
                // ignore
            }

            // Clear local state before redirecting away. Preserve redirectUrl only if it's already set
            // (e.g., token refresh error flow).
            this.clearStoragePreservingRedirectUrl(this.hasRedirectUrl());

            return originalLogout(options);
        };
    }

    private attachEventHandlers(): void {
        effect(() => {
            const event = this.keycloakSignal();
            if (!event) return;

            switch (event.type) {
                case KeycloakEventType.AuthLogout:
                    // If we do receive the event, clear again (idempotent).
                    this.clearStoragePreservingRedirectUrl(false);
                    try {
                        sessionStorage.removeItem(this.logoutMarkerKey);
                    } catch {
                        // ignore
                    }
                    break;

                case KeycloakEventType.AuthRefreshError:
                case KeycloakEventType.AuthError:
                    // Token refresh/auth failures generally mean the session is no longer valid.
                    // Preserve redirectUrl so we can return after re-auth.
                    this.storeCurrentUrlForReAuth();
                    this.clearStoragePreservingRedirectUrl(true);
                    break;

                default:
                    break;
            }
        });
    }

    private clearStoragePreservingRedirectUrl(preserveRedirectUrl: boolean): void {
        let redirectUrl: string | null = null;

        if (preserveRedirectUrl) {
            try {
                redirectUrl = this.storageService.getItem<string>(StorageKey.redirectUrl) ?? null;
            } catch {
                redirectUrl = null;
            }
        }

        this.storageService.clearStorage();

        if (preserveRedirectUrl && redirectUrl) {
            try {
                this.storageService.setItem<string>(StorageKey.redirectUrl, redirectUrl);
            } catch {
                // ignore
            }
        }
    }

    private hasRedirectUrl(): boolean {
        try {
            const url = this.storageService.getItem<string>(StorageKey.redirectUrl);
            return !!url;
        } catch {
            return false;
        }
    }

    private storeCurrentUrlForReAuth(): void {
        try {
            const currentUrl = window.location.pathname + window.location.search + window.location.hash;
            if (!currentUrl || currentUrl === '/' || currentUrl.startsWith('/sign-in') || currentUrl.startsWith('/sign-out')) {
                return;
            }
            this.storageService.setItem<string>(StorageKey.redirectUrl, currentUrl);
        } catch {
            // ignore
        }
    }
}
